/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Accordion, AccordionHeader, AccordionPanel, breakpoints } from '@coursera/cds-core';

import type {
  UserFilterBuilderError,
  UserFilterBuilderErrors,
} from 'bundles/enterprise-admin-user-filters/components/ErrorsNotification';
import type { ActivityFilter } from 'bundles/enterprise-admin-user-filters/types';
import { getDays } from 'bundles/enterprise-admin-user-filters/utils';
import { DebouncedTextField } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type Props = {
  errors: UserFilterBuilderErrors;
  activityFilter: ActivityFilter;
  onActivityFilterChange: (activityFilter: ActivityFilter) => void;
  onClearError: (error: UserFilterBuilderError) => void;
};

const GeneralInactivityFilter = ({ errors, activityFilter, onActivityFilterChange, onClearError }: Props) => {
  const days = getDays(activityFilter.lastActivityDate);
  return (
    <Accordion variant="silent" id="numberOfDaysInactive" defaultExpanded>
      <AccordionHeader
        label={_t(`Inactive for the last #{days} days (including unenrolled learners)`, {
          days,
        })}
      />
      <AccordionPanel css={{ maxWidth: 420 }}>
        <DebouncedTextField
          css={css`
            ${breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
            }
          `}
          label={_t('Inactive for last (days)')}
          supportText={_t(
            'A learner is classified as inactive when they have not engaged in any learning activities: reading, watching videos, or completing assignments'
          )}
          type="number"
          inputProps={{ inputMode: 'numeric' }}
          defaultValue={String(days ?? '')}
          onChange={(value) => {
            onActivityFilterChange({
              ...activityFilter,
              lastActivityDate: { type: 'RELATIVE', nDaysAgoBefore: Number(value) },
            });
            onClearError('numberOfDaysInactive');
          }}
          data-testid="number-of-days-inactive"
          validationLabel={errors.numberOfDaysInactive}
          validationStatus={errors.numberOfDaysInactive ? 'error' : undefined}
        />
      </AccordionPanel>
    </Accordion>
  );
};

export default GeneralInactivityFilter;
